import dayjs, { Dayjs } from 'dayjs';

type DayjsString = {
  type: 'dayjs';
  value: string;
};

export const dayjsToString = (value: Dayjs): DayjsString => {
  return {
    type: 'dayjs',
    value: value.toISOString(),
  };
};

export const stringToDayjs = (stringObject: DayjsString): Dayjs => {
  return dayjs(stringObject.value);
};

export const makeLocalStorable = (payload: object): Record<string, any> => {
  if (typeof payload !== 'object') {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map((item) => makeLocalStorable(item));
  }

  const entries = Object.entries(payload);
  const localStorableObject: Record<string, any> = {};

  for (const [key, value] of entries) {
    const isDayjs = dayjs.isDayjs(value);
    const isArrayOfDayjs = Array.isArray(value) && value.every((item) => dayjs.isDayjs(item));
    let newValue;

    if (isDayjs) {
      newValue = dayjsToString(value);
    } else if (isArrayOfDayjs) {
      newValue = value.map((item) => dayjsToString(item));
    } else if (typeof value === 'object' && value !== null) {
      newValue = makeLocalStorable(value); // Handle nested objects
    } else {
      newValue = value;
    }

    localStorableObject[key] = newValue;
  }

  return localStorableObject;
};

export const parseLocalStorable = (payload: Record<string, any>): Record<string, any> => {
  if (typeof payload !== 'object') {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map((item) => parseLocalStorable(item));
  }

  const entries = Object.entries(payload);
  const parsedObject: Record<string, any> = {};

  for (const [key, value] of entries) {
    if (value?.type === 'dayjs') {
      parsedObject[key] = stringToDayjs(value);
    } else if (Array.isArray(value) && value.every((item) => item.type === 'dayjs')) {
      parsedObject[key] = value.map((item: DayjsString) => stringToDayjs(item));
    } else if (typeof value === 'object' && value !== null) {
      parsedObject[key] = parseLocalStorable(value); // Handle nested objects
    } else {
      parsedObject[key] = value;
    }
  }

  return parsedObject;
};

export function filterUndefinedOrNull<T>(obj: T): NonNullable<T> {
  if (obj === null || obj === undefined) {
    return {} as NonNullable<T>;
  }

  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => filterUndefinedOrNull(item)) as unknown as NonNullable<T>;
  }

  const result = Object.keys(obj).reduce((acc, key) => {
    const value = (obj as any)[key];
    if (value !== null && value !== undefined) {
      if (typeof value === 'object') {
        (acc as any)[key] = filterUndefinedOrNull(value);
      } else {
        (acc as any)[key] = value;
      }
    }
    return acc;
  }, {} as T);

  if (result === null || result === undefined) {
    return {} as NonNullable<T>;
  }

  return result as NonNullable<T>;
}
